/**
 * @file Helpers for attachments
 */

import { __ } from '@@/bits/intl'
import { isPhotoAlbum } from '@@/bits/post_attachment'
import { getHostname } from '@@/bits/url'
import type { PostAttributes, PostColor } from '@@/types'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { LinkHelpers } from '@padlet/beethoven-client'
import type { BeethovenLinkForValidation } from '@padlet/beethoven-client/src/types'
import {
  attachmentFallbackImageUrl,
  fallbackAttachmentSizeMapping,
  FallbackAttachmentType,
  getDurationMetadataText,
  getExtraMetadataText,
  getFallbackAttachmentType,
  getStylizedProviderName,
} from '@padlet/bits'

const BLANK_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=='

const LOWERCASE_PROVIDER_NAMES = {
  YOUTUBE: ['youtube', 'youtu.be'],
}

function isAttachmentBlocked(
  displayAttributes: BeethovenDisplayAttributes,
  blockedContentProviders: Set<string>,
): boolean {
  const lowercaseProviderName = displayAttributes.provider_name?.toLowerCase()
  if (blockedContentProviders.has('youtube') && LOWERCASE_PROVIDER_NAMES.YOUTUBE.includes(lowercaseProviderName)) {
    return true
  }
  return false
}

/**
 * Proxy through Cloudflare worker which adds appropriate headers so that
 * file at `url` is downloaded instead of opened in the browser.
 **/
function downloadUrl(url: string): string {
  const encodedUrl: string = encodeURIComponent(url)
  return `https://padletcdn.com/cgi/fetch?disposition=attachment&url=${encodedUrl}`
}

/**
 * Hosts our Rossini service serves signed uploads from, i.e. Rails' `user_generated_content_domain`:
 * US prod and EU prod (values-prod-eu.yaml), the development default in config/application.yml, and
 * the Cursor/Orchestra dev stack (.cursor/compose.yml). Any other host falls back to the download worker.
 */
const ROSSINI_UPLOAD_HOSTNAMES = new Set([
  'u1.padletusercontent.com',
  'u1-eu.padletusercontent.com',
  'rossini-w3b4ywad5a-uc.a.run.app',
  'rossini.padlet.dev',
])

/**
 * If `url` is a Rossini signed upload URL (`https://<ugc domain>/uploads/...?expiry_token=...`),
 * returns it with `download=1` set so Rossini serves it as an attachment. Rossini names the file from
 * the upload's `Original_Filename` metadata (`filename*=UTF-8''...`), which keeps non-ASCII names
 * like Korean intact; the object name in the URL has those characters replaced with `_`.
 * Returns null for any other URL.
 */
function rossiniSignedUploadDownloadUrl(url: string): string | null {
  let parsedUrl: URL
  try {
    parsedUrl = new URL(url)
  } catch {
    return null
  }

  const isRossiniSignedUpload =
    parsedUrl.protocol === 'https:' &&
    ROSSINI_UPLOAD_HOSTNAMES.has(parsedUrl.hostname) &&
    parsedUrl.pathname.startsWith('/uploads/') &&
    parsedUrl.searchParams.has('expiry_token')
  if (!isRossiniSignedUpload) return null

  if (parsedUrl.searchParams.get('download') === '1') return url

  // Append rather than `searchParams.set`, which would re-serialize (and re-encode) the token.
  parsedUrl.search = `${parsedUrl.search}&download=1`
  return parsedUrl.toString()
}

function postAttachmentDownloadUrl(attachmentHotlink: string | undefined, attachmentUrl: string | undefined): string {
  if (attachmentHotlink != null && attachmentHotlink !== '') {
    return `${attachmentHotlink}&download=1`
  }

  if (attachmentUrl != null && attachmentUrl !== '') {
    return rossiniSignedUploadDownloadUrl(attachmentUrl) ?? downloadUrl(attachmentUrl)
  }
  return ''
}

// When the browser can't render an opened attachment, it will download the file, we should use downloadUrl to preserve the original file name when it contains unicode characters
function urlForOpening(linkData: BeethovenDisplayAttributes | null, displayUrl?: string, hotlink?: string): string {
  if (!displayUrl) {
    return ''
  }

  if (linkData == null) {
    return displayUrl
  }

  const isFile = linkData.content_category === 'file'
  const isDocument = linkData.content_category === 'document'
  const isUpload = linkData.provider_name?.toLowerCase() === 'padlet drive'
  // no embed code or embed code is just downloading the file
  const shouldDownload =
    !linkData.embed_code || linkData.embed_code.includes('https://padlet.com/beethoven/download_file')

  if ((isFile || isDocument) && isUpload && shouldDownload) {
    return postAttachmentDownloadUrl(hotlink, displayUrl)
  }

  return displayUrl
}

function fallbackImageUrl(type: FallbackAttachmentType, colorScheme: 'light' | 'dark', color: PostColor): string {
  return attachmentFallbackImageUrl(type, colorScheme, color)
}

function getMetadataText(link: BeethovenDisplayAttributes): string {
  if (LinkHelpers.isPadletWall(link)) return __('Padlet')
  if (LinkHelpers.isArcade(link)) return __('Arcade')
  if (LinkHelpers.isSlideshow(link)) return __('Slideshow')
  if (LinkHelpers.isSpotify(link)) return getStylizedProviderName(link.provider_name)
  if (LinkHelpers.isYoutube(link)) return getStylizedProviderName(link.provider_name)
  if (LinkHelpers.isAudio(link)) return __('Audio')
  if (LinkHelpers.isUploadedVideo(link)) return __('Video')
  if (LinkHelpers.isPhoto(link)) return __('Image')
  if (LinkHelpers.isPage(link)) {
    return getHostname(link.url).replace(/^www\./, '') || getStylizedProviderName(link.provider_name) || __('webpage')
  }
  if (LinkHelpers.isFile(link) && link.metadata?.pretty_size != null) return link.extension.toUpperCase()
  if (LinkHelpers.isFile(link)) return link.extension.toUpperCase()
  if (LinkHelpers.isGoogleDriveFile(link)) return link.provider_name ?? ''

  return link.extension.toUpperCase() || getStylizedProviderName(link.provider_name) || ''
}

function attachmentLoadingAnimationColorTailwindClass({
  postColor,
  isLightColorScheme,
}: {
  postColor: PostColor
  isLightColorScheme: boolean
}): string {
  switch (postColor) {
    case 'red':
      return isLightColorScheme
        ? 'animate-pulse-bg-color-slow pulse-bg-from-scarlet-200 pulse-bg-to-scarlet-400'
        : 'animate-pulse-bg-color-slow pulse-bg-from-scarlet-800 pulse-bg-to-scarlet-600'
    case 'orange':
      return isLightColorScheme
        ? 'animate-pulse-bg-color-slow pulse-bg-from-canary-200 pulse-bg-to-canary-400'
        : 'animate-pulse-bg-color-slow pulse-bg-from-tangerine-800 pulse-bg-to-tangerine-600'
    case 'green':
      return isLightColorScheme
        ? 'animate-pulse-bg-color-slow pulse-bg-from-park-200 pulse-bg-to-park-400'
        : 'animate-pulse-bg-color-slow pulse-bg-from-park-800 pulse-bg-to-park-600'
    case 'blue':
      return isLightColorScheme
        ? 'animate-pulse-bg-color-slow pulse-bg-from-oceanic-200 pulse-bg-to-oceanic-400'
        : 'animate-pulse-bg-color-slow pulse-bg-from-oceanic-800 pulse-bg-to-oceanic-600'
    case 'purple':
      return isLightColorScheme
        ? 'animate-pulse-bg-color-slow pulse-bg-from-grape-200 pulse-bg-to-grape-400'
        : 'animate-pulse-bg-color-slow pulse-bg-from-grape-800 pulse-bg-to-grape-600'
    default:
      return isLightColorScheme
        ? 'animate-pulse-bg-color-slow pulse-bg-from-grey-50 pulse-bg-to-grey-200'
        : 'animate-pulse-bg-color-slow pulse-bg-from-grey-800 pulse-bg-to-grey-600'
  }
}

function calculateSizeToFitElementInWrapper(
  element: { width: number; height: number; aspectRatio: number },
  wrapper: { width: number; height: number },
): { width: number; height: number; isTouchingHorizontally: boolean; isTouchingVertically: boolean } {
  let width = 0
  let height = 0
  let isTouchingHorizontally = false
  let isTouchingVertically = false

  // Case 1: Element is smaller than wrapper
  // Use element's size
  if (element.width < wrapper.width && element.height < wrapper.height) {
    width = element.width
    height = element.height
    return { width, height, isTouchingHorizontally, isTouchingVertically }
  }

  // Case 2: Element has greater height than wrapper
  // Use wrapper's height and calculate width based on aspect ratio
  if (element.width < wrapper.width && element.height >= wrapper.height) {
    height = wrapper.height
    width = Math.floor(height * element.aspectRatio) // prevent issues with browser from deciding to render pixel fraction weirdly
    isTouchingVertically = true
    return { width, height, isTouchingHorizontally, isTouchingVertically }
  }

  // Case 3: Element has greater width than wrapper
  // Use wrapper's width and calculate height based on aspect ratio
  if (element.width >= wrapper.width && element.height < wrapper.height) {
    width = wrapper.width
    height = Math.floor(width / element.aspectRatio)
    isTouchingHorizontally = true
    return { width, height, isTouchingHorizontally, isTouchingVertically }
  }

  // Case 4: Element is bigger than wrapper
  const wrapperAspectRatio = wrapper.width / wrapper.height

  // Case 4.1: Element is wider than wrapper
  // Use wrapper's width and calculate height based on aspect ratio
  if (wrapperAspectRatio < element.aspectRatio) {
    width = wrapper.width
    height = Math.floor(width / element.aspectRatio)
    isTouchingHorizontally = true
  } else {
    // Case 4.2: Element is taller than wrapper
    // Use wrapper's height and calculate width based on aspect ratio
    height = wrapper.height
    width = Math.floor(height * element.aspectRatio)
    isTouchingVertically = true
  }
  return { width, height, isTouchingHorizontally, isTouchingVertically }
}

// Nitesh's idea is to allow users to download any static attachment. However, we haven't come up
// with a way for the frontend to know which attachment is static yet, so for now, only images (*)
// ,uploads, and links to pdfs can be downloaded.
// (*) Images whose content subcategory is `html` aren't static. They are images from sites like
// Pinterest. We can't download those.
function isAttachmentDownloadable(displayAttributes: BeethovenDisplayAttributes | null): boolean {
  return (
    displayAttributes?.provider_name?.toLowerCase() === 'padlet drive' ||
    (displayAttributes?.content_category === 'photo' &&
      displayAttributes?.content_subcategory?.toLowerCase() !== 'html') ||
    (displayAttributes?.content_category === 'document' &&
      displayAttributes?.content_subcategory?.toLowerCase() === 'pdf')
  )
}

enum StaticAttachmentType {
  PhotoAlbum,
  Image,
  Pdf,
  Unknown,
}
function getStaticAttachmentType(
  displayAttributes: BeethovenDisplayAttributes | null,
  post?: PostAttributes | null,
): StaticAttachmentType {
  if (isPhotoAlbum(post)) {
    return StaticAttachmentType.PhotoAlbum
  }

  if (
    displayAttributes?.content_category === 'photo' &&
    displayAttributes?.content_subcategory?.toLowerCase() !== 'html'
  ) {
    return StaticAttachmentType.Image
  }

  if (
    displayAttributes?.content_category === 'document' &&
    displayAttributes?.content_subcategory?.toLowerCase() === 'pdf'
  ) {
    return StaticAttachmentType.Pdf
  }

  return StaticAttachmentType.Unknown
}

function handleIframeLoad(iframeElement: HTMLIFrameElement | null, onLoad: () => void): void {
  // The iframe may have already loaded before we can add a handler for the load event
  // This would cause the attachment to load endlessly, thus we do a preemptive check here
  const isIframeReady = iframeElement?.contentWindow?.document.readyState === 'complete'
  if (iframeElement == null || isIframeReady) {
    onLoad()
    return
  }

  iframeElement.onload = () => {
    onLoad()
  }
}

function shouldDisplayMetadataTag(displayAttributes: BeethovenDisplayAttributes): boolean {
  const linkForValidation: BeethovenLinkForValidation = {
    content_category: displayAttributes.content_category,
    extension: displayAttributes.extension,
    content_subcategory: displayAttributes.content_subcategory,
    provider_name: displayAttributes.provider_name,
    url: displayAttributes.url,
  }
  if (LinkHelpers.isInstagram(linkForValidation)) return true
  return !LinkHelpers.isPhoto(linkForValidation)
}
export {
  BLANK_IMAGE,
  FallbackAttachmentType,
  fallbackAttachmentSizeMapping,
  getFallbackAttachmentType,
  LOWERCASE_PROVIDER_NAMES,
  isAttachmentBlocked,
  downloadUrl,
  postAttachmentDownloadUrl,
  urlForOpening,
  getStylizedProviderName,
  fallbackImageUrl,
  getMetadataText,
  getDurationMetadataText,
  getExtraMetadataText,
  attachmentLoadingAnimationColorTailwindClass,
  calculateSizeToFitElementInWrapper,
  isAttachmentDownloadable,
  StaticAttachmentType,
  getStaticAttachmentType,
  handleIframeLoad,
  shouldDisplayMetadataTag,
}
